/**
 * RegionCache — a persistent store of serialized CityGraph buffers keyed by
 * (planetSeed, region cell, CITYGRAPH_VERSION), so a revisited planet re-hydrates
 * its settlement regions from disk instead of re-growing them (W2b-2 Task 3).
 *
 * BACKENDS
 * --------
 * In a browser it uses IndexedDB (async, persistent across sessions). In node /
 * tests — where `globalThis.indexedDB` is undefined — it transparently falls back
 * to an in-memory Map with the SAME async surface, so the pure-logic oracles
 * exercise the real get/put contract without a real IDB. The backing Map may be
 * INJECTED (constructor opts) so two RegionCache instances can share one store,
 * which is how a test simulates cross-session persistence.
 *
 * VERSIONING (spec §5.2)
 * ----------------------
 * The key bakes in the CityGraph version: `${planetSeed}:${cellKey}:v${version}`.
 * A CITYGRAPH_VERSION bump (format OR growth-digest change) therefore changes every
 * key, so old buffers become unreachable and a stale graph can never be adopted
 * (see RegionService.adopt's transparency note). Old-version rows are simply
 * orphaned; a future eviction/GC pass (out of scope here) can reclaim them.
 *
 * EVENTUAL CONSISTENCY (documented, deliberate)
 * ---------------------------------------------
 * Every operation is async and fire-and-forget from the LOD hot path: the sync
 * LOD/residency pass NEVER awaits the cache. A miss just materializes synchronously
 * as before; a hit lands a region a few frames later via RegionService.adopt (which
 * is idempotent, so a late hit that races a synchronous materialization is a
 * harmless no-op). Writes are write-behind — the authority is always the synchronous
 * materialization; the cache is a best-effort accelerator for the NEXT visit. IDB
 * errors (private-browsing, quota) are swallowed and degrade to "no cache", never
 * crashing the render loop.
 */

/** Injectable backing store for memory mode (node/tests), so instances can share it. */
export interface RegionCacheOpts {
  /** The in-memory store to use when `globalThis.indexedDB` is undefined. */
  map?: Map<string, ArrayBuffer>;
}

/** IndexedDB object-store name (single store, string keys). */
const STORE = "regions";
/** IndexedDB schema version (bumped only if the store layout below changes). */
const DB_VERSION = 1;

export class RegionCache {
  /** Non-null in memory mode; null when backed by IndexedDB. */
  private readonly memory: Map<string, ArrayBuffer> | null;
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * @param dbName IndexedDB database name (ignored in memory mode).
   * @param opts   memory-mode injection (see {@link RegionCacheOpts}).
   */
  constructor(dbName = "hl-regions", opts?: RegionCacheOpts) {
    this.dbName = dbName;
    // IndexedDB when the environment provides it; else in-memory Map (node/tests).
    // The injected map lets two instances share a store — used by the persistence
    // oracle; ignored under real IDB (which is itself the shared persistent store).
    this.memory = typeof globalThis.indexedDB === "undefined" ? (opts?.map ?? new Map()) : null;
  }

  /** The composite key — version baked in so a bump invalidates every entry. */
  private key(planetSeed: string, cellKey: number, version: number): string {
    return `${planetSeed}:${cellKey}:v${version}`;
  }

  /** Lazily open (and upgrade) the database; one promise, reused. */
  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = globalThis.indexedDB.open(this.dbName, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this.dbPromise;
  }

  /**
   * The cached buffer for (planetSeed, cellKey, version), or null on a miss.
   * A read failure (broken/absent IDB) resolves to null — a miss, never a throw.
   */
  async get(planetSeed: string, cellKey: number, version: number): Promise<ArrayBuffer | null> {
    const k = this.key(planetSeed, cellKey, version);
    if (this.memory) return this.memory.get(k) ?? null;
    try {
      const db = await this.openDb();
      return await new Promise<ArrayBuffer | null>((resolve) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(k);
        req.onsuccess = () => resolve((req.result as ArrayBuffer | undefined) ?? null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  /**
   * Store `buf` under (planetSeed, cellKey, version) (write-behind). Best-effort:
   * a write failure resolves quietly so the fire-and-forget caller never rejects.
   */
  async put(planetSeed: string, cellKey: number, version: number, buf: ArrayBuffer): Promise<void> {
    const k = this.key(planetSeed, cellKey, version);
    if (this.memory) {
      this.memory.set(k, buf);
      return;
    }
    try {
      const db = await this.openDb();
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(buf, k);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
      });
    } catch {
      /* swallow — best-effort accelerator */
    }
  }
}
